import {useQuery} from '@apollo/client';
import {useCallback} from 'react';

import {PREDICTION_BATCH_DETAIL_QUERY} from '../ModelHubQueries';
import type {PredictionBatchStatus} from '../runs/PredictionBatchTypes';
import {STATUS_CODE_TO_STATUS} from '../runs/PredictionBatchTypes';

export interface BatchDetail {
  createdAt: string;
  creatorName: string;
  id: string;
  modelName: string;
  modifiedAt: string;
  name: string;
  numPredictionJobs: number | null;
  status: PredictionBatchStatus;
}

interface PredictionBatchDetailNode {
  createdAt: string;
  creator: {id: string; name: string} | null;
  id: string;
  modelName: string;
  modifiedAt: string;
  name: string;
  numPredictionJobs: number;
  statusCode: string;
}

interface PredictionBatchDetailQueryData {
  predictionBatch: PredictionBatchDetailNode | null;
}

export interface UseBatchDetailResult {
  batch: BatchDetail | null;
  error: string | null;
  isLoading: boolean;
  refresh: () => void;
}

function mapToBatchDetail(node: PredictionBatchDetailNode): BatchDetail {
  return {
    createdAt: node.createdAt ?? '',
    creatorName: node.creator?.name ?? '',
    id: node.id,
    modelName: node.modelName ?? '',
    modifiedAt: node.modifiedAt ?? '',
    name: node.name,
    numPredictionJobs: node.numPredictionJobs ?? null,
    status: STATUS_CODE_TO_STATUS[node.statusCode] ?? 'PENDING',
  };
}

export function useBatchDetail(batchId: string): UseBatchDetailResult {
  const {data, loading, error, refetch} = useQuery<PredictionBatchDetailQueryData>(
    PREDICTION_BATCH_DETAIL_QUERY,
    {variables: {id: batchId}}
  );

  const node = data?.predictionBatch ?? null;

  const refresh = useCallback(() => {
    refetch().catch(() => undefined);
  }, [refetch]);

  return {
    batch: node != null ? mapToBatchDetail(node) : null,
    error: error != null ? error.message : null,
    isLoading: loading,
    refresh,
  };
}
